import React, { useEffect, useState } from "react";
import { C } from "../constants.js";
import { apiFetch } from "../api.js";

export function WebhooksAdminPage({ showNotification }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const data = await apiFetch("/api/admin/webhooks/status");
      setStatus(data);
    } catch (e) {
      showNotification && showNotification(`Failed to load webhook status: ${e.message}`, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStatus(); }, []);

  const handleRegister = async () => {
    setRegistering(true);
    try {
      const data = await apiFetch("/api/admin/webhooks/register", { method: "POST" });
      showNotification && showNotification(
        `Registered ${data.registered} webhook${data.registered === 1 ? "" : "s"}`,
        "success",
      );
      await fetchStatus();
    } catch (e) {
      showNotification && showNotification(`Registration failed: ${e.message}`, "error");
    } finally {
      setRegistering(false);
    }
  };

  if (loading) {
    return React.createElement("div", {
      style: { padding: "40px", textAlign: "center", color: C.textMuted },
    }, "Loading webhook status...");
  }

  const configured = status?.configured;
  const hasRegistrations = (status?.registrations || []).length > 0;

  return React.createElement("div", { style: { padding: "32px 40px", maxWidth: "1100px" } },
    // Header
    React.createElement("div", { style: { marginBottom: "24px" } },
      React.createElement("h1", { style: { margin: 0, color: C.navy, fontSize: "24px", fontWeight: 700 } }, "Webhooks"),
      React.createElement("p", { style: { margin: "6px 0 0", color: C.textMuted, fontSize: "14px", maxWidth: "720px", lineHeight: 1.5 } },
        "Webhooks let Clio push events to the app in real time. When a matter is created, a stage changes, " +
        "a task is completed, or a document is uploaded in Clio, the app reacts automatically " +
        "(provisions folders, creates next tasks, auto-completes related items).",
      ),
    ),

    // Configuration state
    React.createElement("div", {
      style: {
        background: "#fff", borderRadius: "12px", padding: "20px", marginBottom: "16px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)", border: `1px solid ${C.warmGray}`,
      },
    },
      React.createElement("div", { style: { fontSize: "13px", fontWeight: 600, color: C.navy, marginBottom: "12px" } }, "Configuration"),
      React.createElement("div", { style: { display: "grid", gridTemplateColumns: "180px 1fr", gap: "8px 16px", fontSize: "13px" } },
        React.createElement("div", { style: { color: C.textMuted } }, "Webhook URL"),
        React.createElement("div", { style: { color: configured ? C.navy : C.error, fontFamily: "monospace", fontSize: "12px" } },
          status?.webhook_url || "WEBHOOK_BASE_URL not set in environment",
        ),
        React.createElement("div", { style: { color: C.textMuted } }, "Secret"),
        React.createElement("div", { style: { color: status?.secret_set ? C.success : C.error } },
          status?.secret_set ? "Set" : "Not set (signature verification disabled — set WEBHOOK_SECRET in production)",
        ),
        React.createElement("div", { style: { color: C.textMuted } }, "Active subscriptions"),
        React.createElement("div", { style: { color: hasRegistrations ? C.success : C.textMuted } },
          hasRegistrations
            ? `${status.registrations.length} registered`
            : "None — click Register Webhooks to subscribe",
        ),
        React.createElement("div", { style: { color: C.textMuted } }, "Dedupe cache"),
        React.createElement("div", { style: { color: C.textMuted, fontSize: "12px" } },
          `${status?.dedupe?.tracked_event_ids ?? 0} event id${status?.dedupe?.tracked_event_ids === 1 ? "" : "s"} tracked (${Math.round((status?.dedupe?.ttl_seconds || 0) / 60)} min TTL)`,
        ),
      ),
    ),

    // Register / re-register button
    React.createElement("div", { style: { marginBottom: "24px", display: "flex", gap: "12px", alignItems: "center" } },
      React.createElement("button", {
        onClick: handleRegister,
        disabled: !configured || registering,
        style: {
          padding: "10px 24px", borderRadius: "8px", border: "none",
          background: configured && !registering ? C.gold : C.warmGray,
          color: "#fff", fontSize: "14px", fontWeight: 600,
          cursor: configured && !registering ? "pointer" : "not-allowed",
        },
      }, registering ? "Registering..." : (hasRegistrations ? "Re-register webhooks" : "Register webhooks")),
      React.createElement("button", {
        onClick: fetchStatus,
        style: {
          padding: "10px 16px", borderRadius: "8px", border: `1px solid ${C.warmGray}`,
          background: "#fff", color: C.navy, fontSize: "13px", cursor: "pointer",
        },
      }, "Refresh status"),
      !configured ? React.createElement("span", { style: { fontSize: "12px", color: C.error } },
        "Set WEBHOOK_BASE_URL in Railway first",
      ) : null,
    ),

    // Active registrations table
    hasRegistrations ? React.createElement("div", {
      style: {
        background: "#fff", borderRadius: "12px", marginBottom: "24px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)", overflow: "hidden",
      },
    },
      React.createElement("div", { style: { padding: "16px 20px 12px", fontSize: "13px", fontWeight: 600, color: C.navy } }, "Active Registrations"),
      React.createElement("table", { style: { width: "100%", borderCollapse: "collapse", fontSize: "13px" } },
        React.createElement("thead", null,
          React.createElement("tr", { style: { background: C.cream, textAlign: "left" } },
            ...["Model", "Events", "Clio Webhook ID", "Expires", "Created"].map(h =>
              React.createElement("th", { key: h, style: { padding: "10px 16px", color: C.textMuted, fontWeight: 600, fontSize: "11px", textTransform: "uppercase" } }, h),
            ),
          ),
        ),
        React.createElement("tbody", null,
          ...status.registrations.map(r =>
            React.createElement("tr", { key: r.id, style: { borderTop: `1px solid ${C.warmGray}40` } },
              React.createElement("td", { style: { padding: "10px 16px", color: C.navy, fontWeight: 500 } }, r.model),
              React.createElement("td", { style: { padding: "10px 16px", color: C.navy } }, (r.events || []).join(", ")),
              React.createElement("td", { style: { padding: "10px 16px", color: C.textMuted, fontFamily: "monospace", fontSize: "12px" } }, r.clio_webhook_id),
              React.createElement("td", { style: { padding: "10px 16px", color: C.textMuted } }, r.expires_at ? new Date(r.expires_at).toLocaleDateString() : "—"),
              React.createElement("td", { style: { padding: "10px 16px", color: C.textMuted } }, r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"),
            ),
          ),
        ),
      ),
    ) : null,

    // Recent events received
    React.createElement("div", {
      style: {
        background: "#fff", borderRadius: "12px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)", overflow: "hidden",
      },
    },
      React.createElement("div", { style: { padding: "16px 20px 12px", fontSize: "13px", fontWeight: 600, color: C.navy } },
        "Recent Webhook-Driven Events",
        React.createElement("span", { style: { color: C.textMuted, fontWeight: 400, marginLeft: "8px" } },
          "(from audit log — last 20)",
        ),
      ),
      (status?.recent_events || []).length === 0
        ? React.createElement("div", { style: { padding: "24px 20px", color: C.textMuted, fontSize: "13px" } },
            "No webhook-driven events yet. After registering, trigger an event in Clio (e.g. update a matter's status) to verify events arrive.",
          )
        : React.createElement("table", { style: { width: "100%", borderCollapse: "collapse", fontSize: "13px" } },
            React.createElement("thead", null,
              React.createElement("tr", { style: { background: C.cream, textAlign: "left" } },
                ...["When", "Event", "Matter", "Description"].map(h =>
                  React.createElement("th", { key: h, style: { padding: "10px 16px", color: C.textMuted, fontWeight: 600, fontSize: "11px", textTransform: "uppercase" } }, h),
                ),
              ),
            ),
            React.createElement("tbody", null,
              ...status.recent_events.map(ev =>
                React.createElement("tr", { key: ev.id, style: { borderTop: `1px solid ${C.warmGray}40` } },
                  React.createElement("td", { style: { padding: "10px 16px", color: C.textMuted, whiteSpace: "nowrap" } },
                    ev.created_at ? new Date(ev.created_at).toLocaleString() : "—"),
                  React.createElement("td", { style: { padding: "10px 16px", color: C.navy, fontWeight: 500 } }, ev.event_type),
                  React.createElement("td", { style: { padding: "10px 16px", color: C.navy } }, ev.matter_name || ev.matter_id || "—"),
                  React.createElement("td", { style: { padding: "10px 16px", color: C.textMuted } }, ev.description),
                ),
              ),
            ),
          ),
    ),
  );
}


